import React, { useState } from 'react'
import { Ticket, Copy, Download, X, Plus } from 'lucide-react'

const CodeGenerateModal = ({ products, onGenerate, onClose, loading }) => {
  const [productId, setProductId] = useState('')
  const [count, setCount] = useState(10)
  const [expiry, setExpiry] = useState('never')
  const [customDate, setCustomDate] = useState('')
  const [notes, setNotes] = useState('')
  const [generatedCodes, setGeneratedCodes] = useState(null)

  const handleGenerate = async () => {
    let expiresAt = null
    if (expiry === '7d') expiresAt = new Date(Date.now() + 7 * 86400000).toISOString()
    else if (expiry === '30d') expiresAt = new Date(Date.now() + 30 * 86400000).toISOString()
    else if (expiry === '90d') expiresAt = new Date(Date.now() + 90 * 86400000).toISOString()
    else if (expiry === 'custom' && customDate) expiresAt = new Date(customDate).toISOString()

    const result = await onGenerate(parseInt(productId), count, expiresAt, notes)
    if (result?.codes) {
      setGeneratedCodes(result.codes)
    }
  }

  const formatCode = (code) => {
    if (!code || code.length !== 15) return code
    return `${code.slice(0, 3)}-${code.slice(3, 7)}-${code.slice(7, 11)}-${code.slice(11)}`
  }

  const copyAll = () => {
    if (!generatedCodes) return
    const text = generatedCodes.map(formatCode).join('\n')
    navigator.clipboard.writeText(text)
  }

  const downloadCSV = () => {
    if (!generatedCodes) return
    const selectedProduct = products.find(p => p.id === parseInt(productId))
    const header = 'Code,Product,Generated At\n'
    const rows = generatedCodes.map(c =>
      `${formatCode(c)},${selectedProduct?.name || ''},${new Date().toISOString()}`
    ).join('\n')
    const blob = new Blob([header + rows], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `g2bulk-codes-${Date.now()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const downloadTXT = () => {
    if (!generatedCodes) return
    const text = generatedCodes.map(formatCode).join('\n')
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `g2bulk-codes-${Date.now()}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in">
        {!generatedCodes ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Ticket size={20} className="text-accent" />
                <h2 className="text-lg font-bold text-text-primary">Generate Codes</h2>
              </div>
              <button 
                onClick={onClose} 
                className="text-text-muted hover:text-text-primary transition-colors p-1"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="label mb-2 block">Product *</label>
                <select
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  className="input"
                  required
                >
                  <option value="">Select a product...</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.denomination}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label mb-2 block">Number of Codes</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={count}
                    onChange={(e) => setCount(Math.min(1000, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="input flex-1"
                  />
                  <span className="text-text-muted text-sm">max 1,000</span>
                </div>
              </div>

              <div>
                <label className="label mb-2 block">Expiry</label>
                <select
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                  className="input"
                >
                  <option value="never">Never expires</option>
                  <option value="7d">7 days from now</option>
                  <option value="30d">30 days from now</option>
                  <option value="90d">90 days from now</option>
                  <option value="custom">Custom date</option>
                </select>
                {expiry === 'custom' && (
                  <input
                    type="datetime-local"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="input mt-2"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                )}
              </div>

              <div>
                <label className="label mb-2 block">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Batch notes or description..."
                  className="input h-20 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-8 pt-4 border-t border-border">
              <button
                onClick={handleGenerate}
                disabled={!productId || loading}
                className="btn btn-primary flex-1"
              >
                {loading ? (
                  <span className="flex items-center justify-center space-x-2">
                    <span className="spinner w-4 h-4"></span>
                    <span>Generating...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center space-x-2">
                    <Plus size={16} />
                    <span>Generate {count} Codes</span>
                  </span>
                )}
              </button>
              <button onClick={onClose} className="btn btn-secondary">Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Ticket size={20} className="text-success" />
                <h2 className="text-lg font-bold text-text-primary">
                  {generatedCodes.length} Codes Generated
                </h2>
              </div>
              <button 
                onClick={onClose} 
                className="text-text-muted hover:text-text-primary transition-colors p-1"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex items-center space-x-3 mb-4">
              <button onClick={copyAll} className="btn btn-primary">
                <Copy size={16} className="mr-2" />
                Copy All
              </button>
              <button onClick={downloadCSV} className="btn btn-secondary">
                <Download size={16} className="mr-2" />
                CSV
              </button>
              <button onClick={downloadTXT} className="btn btn-secondary">
                <Download size={16} className="mr-2" />
                TXT
              </button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto border">
              <div className="space-y-2">
                {generatedCodes.map((code, i) => (
                  <div 
                    key={i} 
                    className="font-mono text-sm text-accent select-all hover:bg-white p-2 rounded transition-colors cursor-pointer"
                    title="Click to select"
                  >
                    {formatCode(code)}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 p-3 bg-accent/5 border border-accent/20 rounded-lg">
              <p className="text-sm text-text-secondary">
                💡 <strong>Tip:</strong> These codes are now active and ready to redeem. 
                You can copy individual codes by clicking them or use the buttons above for bulk actions.
              </p>
            </div>

            <button onClick={onClose} className="btn btn-primary w-full mt-6">
              Done
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default CodeGenerateModal